import React, { useState, useEffect } from "react";
import api from "../services/api";
import MainCarousel from "../components/user/MainCarousel";
import DateTabs from "../components/user/DateTabs";
import { useData } from "../components/context/data/DataState";
import { useAuth } from "../components/context/auth/AuthState";
import ActivitiesLinear from "../components/user/ActivitiesLinear";
import DateTabsActivity from "../components/user/DateTabsActivity";
import { useParams, useNavigate } from "react-router-dom";
import { Separator } from "../components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { X, AlertCircle, CheckCircle2, ChevronLeft, Upload, FileImage, Pencil, Calendar, Trophy } from "lucide-react";
import { PickActivityData } from "../components/backendRequests/functionUtils";

const EditChallenge = () => {
  const { activityId: paramActivityId } = useParams();
  const {
    date,
    setdate,
    communityId,
    setCommunityId,
    seasonId,
    setSeasonId,
    challengeId,
    setChallengeId,
    data,
    activityId,
    setactivityId,
    activityStatusData,
    formattedDate,
    fetchActivityStatusData,
    updateTrackerData,
  } = useData();

  const { currentUser } = useAuth();
  let navigate = useNavigate();

  const [activityCompleted, setActivityCompleted] = useState(false);
  const [exerciseDetails, setExerciseDetails] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [update, setUpdate] = useState(false);
  const [status, setstatus] = useState({
    severity: "error",
    title: "",
    msg: "",
    display: false,
  });
  const [disabled, setDisabled] = useState(false);
  const [file, setFile] = useState(null);
  const [submittedFile, setSubmittedFile] = useState(null);
  const [activityData, setActivityData] = useState(null);

  // New state for view/edit mode
  const [isEditing, setIsEditing] = useState(true);

  // Sync param ID with context and find activity in data tree
  useEffect(() => {
    if (paramActivityId && paramActivityId !== activityId) {
      setactivityId(paramActivityId);
    }
  }, [paramActivityId, activityId, setactivityId]);

  // Find activity data and parent IDs when data or activityId changes
  useEffect(() => {
    if (!data || !data.community || !paramActivityId) return;

    // Helper to find activity in the data tree
    const findActivity = () => {
      for (const cId in data.community) {
        const community = data.community[cId];
        for (const sId in community.season) {
          const season = community.season[sId];
          for (const chId in season.challenge) {
            const challenge = season.challenge[chId];

            // Check events
            const event = challenge.event?.find(e => String(e.event_id) === String(paramActivityId));
            if (event) return { activity: event, cId, sId, chId };

            // Check tasks
            const task = challenge.task?.find(t => String(t.task_id) === String(paramActivityId));
            if (task) return { activity: task, cId, sId, chId };
          }
        }
      }
      return null;
    };

    const found = findActivity();
    if (found) {
      setActivityData(found.activity);
      // Update context IDs if they don't match (handles refresh)
      if (communityId !== found.cId) setCommunityId(found.cId);
      if (seasonId !== found.sId) setSeasonId(found.sId);
      if (challengeId !== found.chId) setChallengeId(found.chId);
    }
  }, [data, paramActivityId]); // Removed dependencies that cause loops

  const handleFileChange = (e) => {
    if (e.target.files[0]?.size > 1000000) {
      setstatus((prev) => ({
        ...prev,
        severity: "error",
        title: "Error",
        msg: "File size should be less than 1MB",
        display: true,
      }));
      return;
    }
    setFile(e.target.files[0]);
    setUpdate(true);
  };

  useEffect(() => {
    setUpdate(false);
    if (!paramActivityId || !activityStatusData) return;

    const result = PickActivityData(
      activityStatusData,
      currentUser.user_id,
      paramActivityId,
      formattedDate
    );

    if (result === null) {
      setActivityCompleted(false);
      setExerciseDetails("");
      setSubmitted(false);
      setSubmittedFile(null);
      setIsEditing(true); // Default to edit mode if not submitted
    } else {
      setActivityCompleted(result.activity_completed === 1 || result.activity_completed === true);
      setExerciseDetails(result.quantity || "");
      setSubmitted(true);
      setSubmittedFile(result.image);
      setIsEditing(false); // Default to view mode if submitted
    }
  }, [paramActivityId, activityStatusData, formattedDate, currentUser.user_id]);

  useEffect(() => {
    if (status.display === false) return;
    const timer = setTimeout(() => {
      setstatus((prev) => ({ ...prev, display: false }));
    }, 5000);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDisabled(true);

    const formData = new FormData();
    formData.append("user_id", currentUser.user_id);
    formData.append("activity_id", paramActivityId);
    formData.append("challenge_id", challengeId);
    formData.append("season_id", seasonId);
    formData.append("date", formattedDate.toISOString().split("T")[0]);
    formData.append("timestamp", new Date().toISOString());
    formData.append("activity_completed", activityCompleted ? 1 : 0);
    formData.append("quantity", exerciseDetails);
    if (file) {
      formData.append("image", file);
    }

    api.post("/activity/update", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        setstatus({
          severity: "success",
          title: "Success",
          msg: "Activity Updated Successfully",
          display: true,
        });
        setSubmitted(true);
        setUpdate(false);
        setFile(null);
        fetchActivityStatusData();
        updateTrackerData("activity", paramActivityId);
        setIsEditing(false); // Switch to view mode on success
      })
      .catch((error) => {
        setstatus({
          severity: "error",
          title: "Error",
          msg: error.response?.data || "Failed to update activity",
          display: true,
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  return (
    <div className="pb-24 min-h-screen bg-gray-50/50">
      <MainCarousel />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <Card className="shadow-lg border-none">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 text-gray-500 hover:text-gray-900"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft className="h-5 w-5 mr-1" />
                Back
              </Button>
              {activityData && (
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  {activityData.task_name ? 'Task' : 'Event'}
                </span>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900 mt-2">
              {activityData ? (activityData.task_name || activityData.event_name) : 'Loading...'}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <DateTabs />
            <ActivitiesLinear />

            {/* Status Alert */}
            {status.display && (
              <Alert variant={status.severity === "error" ? "destructive" : "default"} className={status.severity === "success" ? "border-green-500 bg-green-50 text-green-700" : ""}>
                {status.severity === "error" ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
                <AlertTitle>{status.title}</AlertTitle>
                <AlertDescription>{status.msg}</AlertDescription>
              </Alert>
            )}

            {activityData && (
              <>
                {!isEditing && submitted ? (
                  // View Mode
                  <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-white p-6 rounded-xl border shadow-sm space-y-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Submission Details</h3>
                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${activityCompleted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {activityCompleted ? (
                            <>
                              <CheckCircle2 className="h-4 w-4" />
                              <span>Completed</span>
                            </>
                          ) : (
                            <>
                              <X className="h-4 w-4" />
                              <span>Incomplete</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                          <label className="text-sm text-gray-500 font-medium">Quantity / Details</label>
                          <p className="text-gray-900 font-medium text-lg">
                            {exerciseDetails || "No details provided"}
                            {exerciseDetails && activityData.task_unit && <span className="text-gray-500 text-sm ml-1">{activityData.task_unit}</span>}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <label className="text-sm text-gray-500 font-medium">Date</label>
                          <div className="flex items-center gap-2 text-gray-900">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>{formattedDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>

                      {submittedFile && (
                        <div className="space-y-2">
                          <label className="text-sm text-gray-500 font-medium">Proof of Completion</label>
                          <div className="relative group rounded-lg overflow-hidden border bg-gray-50 max-w-sm">
                            <img
                              src={submittedFile}
                              alt="Proof"
                              className="w-full h-auto object-cover max-h-[300px]"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://placehold.co/600x400?text=Image+Not+Found";
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => setIsEditing(true)}
                      className="w-full h-12 text-lg font-medium"
                      variant="outline"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Activity
                    </Button>
                  </div>
                ) : (
                  // Edit Mode
                  <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-white p-6 rounded-xl border shadow-sm space-y-6">
                      {/* Completion Checkbox */}
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <Checkbox
                          id="completed"
                          checked={activityCompleted}
                          onCheckedChange={(checked) => {
                            setActivityCompleted(checked);
                            setUpdate(true);
                          }}
                          className="h-6 w-6"
                        />
                        <div className="space-y-1">
                          <Label
                            htmlFor="completed"
                            className="text-base font-medium leading-none cursor-pointer"
                          >
                            Mark as Completed
                          </Label>
                          <p className="text-sm text-gray-500">
                            Check this box when you've finished the activity
                          </p>
                        </div>
                      </div>

                      {/* Quantity Input */}
                      <div className="space-y-2">
                        <Label htmlFor="quantity" className="text-sm font-medium">
                          Quantity / Details
                        </Label>
                        <Input
                          id="quantity"
                          type="text"
                          value={exerciseDetails}
                          onChange={(e) => {
                            setExerciseDetails(e.target.value);
                            setUpdate(true);
                          }}
                          placeholder={activityData.task_unit ? `Enter amount in ${activityData.task_unit}` : "Enter details"}
                          className="w-full"
                        />
                      </div>

                      {/* File Upload */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Proof of Completion (Optional)</Label>
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div className="flex flex-col items-center gap-2">
                            <div className="bg-blue-50 p-3 rounded-full">
                              <Upload className="h-6 w-6 text-blue-500" />
                            </div>
                            <p className="text-sm font-medium text-gray-700">
                              {file ? file.name : "Click to upload image"}
                            </p>
                            <p className="text-xs text-gray-500">Max file size: 1MB</p>
                          </div>
                        </div>

                        {/* File Preview / Remove */}
                        {(file || submittedFile) && (
                          <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                            <div className="flex items-center gap-3">
                              <FileImage className="h-5 w-5 text-blue-500" />
                              <span className="text-sm text-gray-700 truncate max-w-[200px]">
                                {file ? file.name : "Previous Upload"}
                              </span>
                            </div>
                            {file && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setFile(null);
                                  setUpdate(true);
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      {submitted && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsEditing(false)}
                          className="flex-1 h-12 text-lg font-medium"
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        type="submit"
                        disabled={disabled || !update}
                        className={`h-12 text-lg font-medium shadow-md hover:shadow-lg transition-all ${submitted ? 'flex-1' : 'w-full'}`}
                        size="lg"
                      >
                        {submitted ? "Update Activity" : "Submit Activity"}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditChallenge;
